import { type QueryObserverResult, type RefetchOptions } from '@tanstack/react-query';
import { TradingToast, useToast, useTradingTheme } from '@trading-ui/core';
import type { CellComponent, Options, TradingDatagridV3Interface } from '@trading-ui/datagrid-v3';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounceCallback } from 'usehooks-ts';

import { useConsumerViewParams } from '@/views/consumer_view/hooks';
import useConsumerTableStore from '@/views/consumer_view/store/consumer-table.store';
import useConsumerStore from '@/views/consumer_view/store/consumer.store';

import { updateConsumptions } from '@/api/services/consumer/consumer.service';
import { apiRoutes } from '@/config/apiRoutes';
import { parseBackendReason } from '@/utils/error.utils';

import type { PricingIndex } from '@/api/services/settings/pricing-indices.service';
import type {
  Consumption,
  ConsumptionChartResponse,
  ConsumptionTableRow,
  Plant,
  ProcessedBackendError,
} from '@/interfaces';
import { isGroupedConsumption } from '@/interfaces';
import type { ConsumerViewParams } from '@/views/consumer_view/types/consumer.types';

import { getColumns } from '../constants/columns';

// Helper function to check if a column field is a nomination column
const isNominationColumn = (field: string | undefined): boolean => {
  if (!field) return false;
  return field === 'nomination' || field.startsWith('nomination_');
};

// Helper function to extract plantId from dynamic nomination column field
const getPlantIdFromField = (field: string): number | null => {
  if (field === 'nomination') return null;
  const match = field.match(/^nomination_(\d+)$/);
  return match ? parseInt(match[1], 10) : null;
};

type Params = {
  mutateChart: (options?: RefetchOptions) => Promise<QueryObserverResult<ConsumptionChartResponse, Error>>;
  height: number;
  pricingIndices: PricingIndex[];
  plantPricingIndexMap: Map<number, number | null>;
  duplicateIdentifiers: Set<string>;
  duplicatePlants: Array<Plant & { companyName?: string }>;
};

type PastedCellValue = number | null;

// Function to transform the parsed text on Table to an array of numbers
const transformPastedText = (pastedText: string): (number | null)[][] => {
  const transformedArray = pastedText
    // Normalize all line endings to \n
    .replace(/\r\n?|\n/g, '\n')
    .replace(/\n$/, '') // Ensure trailing newline for consistent splitting
    .split('\n') // Splits by rows (new line)
    .map((row: string) =>
      row
        // .replace(/\r$/, '')
        .split(/\s+/) // Splits by spaces or tabs
        .map((val: string) => {
          const trimmed = val.trim();
          if (trimmed === '') return null;

          let normalized = trimmed.replace(/\./g, '');
          normalized = normalized.replace(/,/g, '');
          normalized = normalized.replace(/\s/g, '');

          const parsed = parseFloat(normalized);
          return parsed;
        })
    );

  return transformedArray;
};

const buildUpdateUrlParams = (params: ConsumerViewParams | null) => {
  if (!params) return '';

  const urlParams = new URLSearchParams();

  urlParams.append('startDate', params.startDate);
  urlParams.append('endDate', params.endDate);

  if (Object.keys(params.cups).length > 0) {
    urlParams.append('filters', JSON.stringify(params.cups));
  }

  return `${apiRoutes.base}${apiRoutes.consumption.base}${apiRoutes.consumption.update}?${urlParams.toString()}`;
};

export const useConsumptionTable = ({
  mutateChart,
  height,
  pricingIndices,
  plantPricingIndexMap,
  duplicateIdentifiers,
  duplicatePlants,
}: Params) => {
  const theme = useTradingTheme();
  const { addToast } = useToast();
  const { i18n, t } = useTranslation();

  const tableInstance = useConsumerTableStore((state) => state.tableInstance);
  const setAverageNomination = useConsumerStore((state) => state.setAverageNomination);
  const setAverageEnagas = useConsumerStore((state) => state.setAverageEnagas);
  const saveTableInstance = useConsumerTableStore((state) => state.saveTableInstance);
  const params = useConsumerViewParams();
  const url = buildUpdateUrlParams(params);
  const [isUpdating, setIsUpdating] = useState(false);

  // Track current minute to auto-refresh columns when time changes
  const [currentMinute] = useState(() => {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  });

  // Regenerate columns reactively when language changes or time changes
  const columns = useMemo(
    () => getColumns(pricingIndices, plantPricingIndexMap, duplicateIdentifiers, duplicatePlants),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language, pricingIndices, plantPricingIndexMap, currentMinute, duplicateIdentifiers, duplicatePlants]
  );

  const handleUpdateConsumptions = useCallback(
    async (
      updates: Array<{
        id: number;
        nomination: number | null;
        plantId: number;
        date: Date;
      }>
    ) => {
      if (updates.length === 0) return;

      setIsUpdating(true);

      try {
        // Update Consumptions
        const { consumptions, meta } = await updateConsumptions(updates, url);

        // Refresh data after all updates are complete
        await mutateChart();

        const isGroupedMode = duplicateIdentifiers.size > 0;

        if (isGroupedMode && tableInstance) {
          // In grouped mode, updateData can't match rows by ID because grouped rows
          // don't have a single ID. We need to manually update each affected grouped row.
          const tableRows = tableInstance.getRows();

          tableRows.forEach((row) => {
            const rowData = row.getData() as ConsumptionTableRow;
            if (!isGroupedConsumption(rowData)) return;

            // Check if any returned consumption belongs to this grouped row
            const matchingConsumptions = consumptions.filter((c) => {
              const cDateStr = (c.date instanceof Date ? c.date : new Date(c.date)).toISOString().split('T')[0];
              const rowDateStr = (rowData.date instanceof Date ? rowData.date : new Date(rowData.date))
                .toISOString()
                .split('T')[0];
              return cDateStr === rowDateStr;
            });

            if (matchingConsumptions.length === 0) return;

            // Build updated row data
            const updatedConsumptions = rowData.consumptions.map((rc) => {
              const updated = matchingConsumptions.find((mc) => mc.plant.id === rc.plant.id);
              return updated || rc;
            });

            const updatedRowData: Record<string, unknown> = {
              ...rowData,
              consumptions: updatedConsumptions,
              totalNomination: matchingConsumptions[0].totalNomination,
              totalDifference: matchingConsumptions[0].totalDifference,
              enagas: matchingConsumptions[0].enagas,
            };

            // Update dynamic nomination fields
            updatedConsumptions.forEach((c) => {
              updatedRowData[`nomination_${c.plant.id}`] = c.nomination;
            });

            row.update(updatedRowData);
          });
        } else if (tableInstance) {
          const getDateKey = (date: Date | string) =>
            (date instanceof Date ? date : new Date(date)).toISOString().split('T')[0];

          const updatedById = new Map(consumptions.map((consumption) => [consumption.id, consumption]));
          const updatedByPlantAndDate = new Map(
            consumptions.map((consumption) => [`${consumption.plant.id}_${getDateKey(consumption.date)}`, consumption])
          );

          tableInstance.getRows().forEach((row) => {
            const rowData = row.getData() as Consumption;
            const updatedConsumption =
              updatedById.get(rowData.id) ||
              updatedByPlantAndDate.get(`${rowData.plant.id}_${getDateKey(rowData.date)}`);

            if (updatedConsumption) {
              row.update(updatedConsumption);
            }
          });
        }

        setAverageNomination(meta.averages?.nomination ?? null);
        setAverageEnagas(meta.averages?.enagas ?? null);

        addToast(
          <TradingToast
            type="success"
            title={t('services.updateConsumptions.sucessTitle')}
            message={t('services.updateConsumptions.successMessage', { count: updates.length })}
          />
        );
      } catch (error) {
        addToast(
          <TradingToast
            type="error"
            title="Error"
            message={parseBackendReason(error as Error | ProcessedBackendError)}
          />
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [addToast, duplicateIdentifiers, mutateChart, setAverageEnagas, setAverageNomination, t, tableInstance, url]
  );

  // Ref to always access the latest handleUpdateConsumptions in event handlers
  const handleUpdateConsumptionsRef = useRef(handleUpdateConsumptions);
  useEffect(() => {
    handleUpdateConsumptionsRef.current = handleUpdateConsumptions;
  }, [handleUpdateConsumptions]);

  const keyboardHandlerRef = useRef<((e: KeyboardEvent) => void) | null>(null);

  // Cleanup keyboard handler on unmount
  useEffect(
    () => () => {
      if (keyboardHandlerRef.current) {
        document.removeEventListener('keydown', keyboardHandlerRef.current);
        keyboardHandlerRef.current = null;
      }
    },
    []
  );

  const getTableInstance = useCallback(
    (table: TradingDatagridV3Interface) => {
      if (table) {
        // Helper to get the currently active cell from Tabulator's range API
        // This is always in sync with keyboard navigation and mouse selection
        const getActiveCellFromRange = (): CellComponent | null => {
          const ranges = table.getRanges();
          if (!ranges || ranges.length === 0) return null;

          const range = ranges[ranges.length - 1];
          const cells = range.getCells() as unknown as CellComponent[][];
          if (!cells || cells.length === 0) return null;

          // Get the last cell in the range (active/anchor cell)
          const lastRow = cells[cells.length - 1];
          if (!lastRow || lastRow.length === 0) return null;
          return lastRow[lastRow.length - 1];
        };

        // Preserve current cell position for restoration after data refresh
        const preserveCurrentPosition = (cell: CellComponent) => {
          const currentRow = cell.getRow();
          const column = cell.getColumn();
          const columnField = column.getField();
          const allRows = table.getRows();

          const currentIndex = allRows.findIndex((r) => r === currentRow);
          if (currentIndex === -1) return;

          const store = useConsumerTableStore.getState();
          store.setLastEditedRowIndex(currentIndex);
          store.setLastEditedColumnField(columnField || 'nomination');
          store.setRestoreSelectionUntil(Date.now() + 3000);
        };

        const restorePendingSelection = () => {
          const store = useConsumerTableStore.getState();
          const { lastEditedRowIndex, lastEditedColumnField, restoreSelectionUntil } = store;

          if (lastEditedRowIndex === null || !restoreSelectionUntil) return;
          if (Date.now() > restoreSelectionUntil) {
            store.setLastEditedRowIndex(null);
            store.setLastEditedColumnField(null);
            store.setRestoreSelectionUntil(null);
            return;
          }

          // Only restore if selection was lost (ranges are empty)
          const currentRanges = table.getRanges();
          if (currentRanges && currentRanges.length > 0) {
            // Selection still exists, clear pending restore
            store.setLastEditedRowIndex(null);
            store.setLastEditedColumnField(null);
            store.setRestoreSelectionUntil(null);
            return;
          }

          const rows = table.getRows();
          if (lastEditedRowIndex >= rows.length) return;

          const targetRow = rows[lastEditedRowIndex];
          if (!targetRow) return;

          const targetCell = targetRow.getCell(lastEditedColumnField || 'nomination');
          if (!targetCell) return;

          // Clear previous ranges and select only the target cell
          const ranges = table.getRanges();
          ranges?.forEach((range) => range.remove());
          table.addRange(targetCell, targetCell);

          // Clear the pending restore state
          store.setLastEditedRowIndex(null);
          store.setLastEditedColumnField(null);
          store.setRestoreSelectionUntil(null);
        };

        // Single global keyboard handler that reads the active cell from Tabulator's range API
        const globalKeydownHandler = (keyEvent: KeyboardEvent) => {
          // Check if the event target is inside the table container
          const target = keyEvent.target as HTMLElement;
          const tableElement = table.element;
          if (!tableElement || !target || !tableElement.contains(target)) {
            // Event is outside this table, ignore it
            return;
          }

          // Check if we're currently editing any cell
          const editingElements = document.querySelectorAll('.tabulator-editing');
          if (editingElements.length > 0) {
            // Allow normal typing when editing
            return;
          }

          // Get the currently selected cell from Tabulator's range system
          // This correctly reflects keyboard navigation position
          const activeCell = getActiveCellFromRange();
          if (!activeCell) {
            return;
          }

          const column = activeCell.getColumn();
          const columnField = column.getField();

          if (!isNominationColumn(columnField)) {
            return;
          }

          const cellElement = activeCell.getElement();
          if (!cellElement) return;

          const { key } = keyEvent;

          // Handle Delete or Backspace to clear the cell
          if (key === 'Delete' || key === 'Backspace') {
            keyEvent.preventDefault();
            keyEvent.stopPropagation();

            const row = activeCell.getRow();
            const rowData = row.getData() as ConsumptionTableRow;

            // Handle grouped rows
            if (isGroupedConsumption(rowData)) {
              const plantId = getPlantIdFromField(columnField || '');
              if (!plantId) return;

              const consumption = rowData.consumptions.find((c) => c.plant.id === plantId);
              if (!consumption) return;

              if (consumption.nomination !== null && consumption.nomination !== undefined) {
                preserveCurrentPosition(activeCell);
                handleUpdateConsumptionsRef.current([
                  {
                    id: consumption.id,
                    nomination: null,
                    plantId: consumption.plant.id,
                    date: consumption.date instanceof Date ? consumption.date : new Date(consumption.date),
                  },
                ]);
              }
              return;
            }

            // Handle non-grouped rows
            const consumptionData = rowData as Consumption;
            if (consumptionData.nomination !== null && consumptionData.nomination !== undefined) {
              preserveCurrentPosition(activeCell);
              handleUpdateConsumptionsRef.current([
                {
                  id: consumptionData.id,
                  nomination: null,
                  plantId: consumptionData.plant.id,
                  date: consumptionData.date instanceof Date ? consumptionData.date : new Date(consumptionData.date),
                },
              ]);
            }
            return;
          }

          // Handle number keys (0-9), period, minus to start editing
          const isNumber = /^[0-9]$/.test(key);
          const isPunctuation = key === '.' || key === ',' || key === '-';

          if (isNumber || isPunctuation) {
            keyEvent.preventDefault();
            keyEvent.stopPropagation();

            // Start editing the active cell (from range, not from mouse click)
            activeCell.edit();

            // Wait for editor to be ready, then set the value
            setTimeout(() => {
              const input = cellElement.querySelector('input');
              if (input) {
                const htmlInput = input as HTMLInputElement;
                htmlInput.value = key === ',' ? '.' : key;
                htmlInput.focus();
                if (htmlInput.type !== 'number') {
                  htmlInput.setSelectionRange(1, 1);
                }
              }
            }, 10);
          }
        };

        // Remove any previous handler before adding a new one
        if (keyboardHandlerRef.current) {
          document.removeEventListener('keydown', keyboardHandlerRef.current);
        }

        // Add single global keyboard handler once
        keyboardHandlerRef.current = globalKeydownHandler;
        document.addEventListener('keydown', globalKeydownHandler);

        // Restore selection after redraws/data processing
        table.on('renderComplete', restorePendingSelection);
        table.on('dataProcessed', restorePendingSelection);
        table.on('tableBuilt', restorePendingSelection);

        // Cleanup listeners when table is destroyed
        table.on('tableDestroyed', () => {
          if (keyboardHandlerRef.current) {
            document.removeEventListener('keydown', keyboardHandlerRef.current);
            keyboardHandlerRef.current = null;
          }
          try {
            table.off('renderComplete', restorePendingSelection);
            table.off('dataProcessed', restorePendingSelection);
            table.off('tableBuilt', restorePendingSelection);
          } catch {
            // noop
          }
        });

        // Handle cell click: clear any pending programmatic restore
        table.on('cellClick', () => {
          // User interaction overrides any pending programmatic restore
          const store = useConsumerTableStore.getState();
          if (store.lastEditedRowIndex !== null || store.restoreSelectionUntil !== null) {
            store.setLastEditedRowIndex(null);
            store.setLastEditedColumnField(null);
            store.setRestoreSelectionUntil(null);
          }
        });

        table.on('cellEdited', (cell: CellComponent) => {
          const column = cell.getColumn();
          const columnField = column.getField();

          if (!isNominationColumn(columnField)) {
            return;
          }

          const row = cell.getRow();
          const rowData = row.getData() as ConsumptionTableRow;
          let newValue = cell.getValue() as number | string | null;
          let oldValue = cell.getOldValue() as number | string | null;

          // Handle empty string as null (cleared value)
          if (newValue === '' || newValue === undefined) {
            newValue = null;
          }
          // Convert string to number if needed
          else if (typeof newValue === 'string') {
            const trimmed = newValue.trim();
            if (trimmed === '') {
              newValue = null;
            } else {
              const parsed = parseFloat(trimmed);
              if (Number.isNaN(parsed)) {
                cell.restoreOldValue();
                return;
              }
              newValue = parsed;
            }
          }
          // Validate that we have a valid number (including 0) or null
          else if (newValue !== null && (typeof newValue !== 'number' || Number.isNaN(newValue))) {
            cell.restoreOldValue();
            return;
          }

          // Normalize old value
          if (oldValue === '' || oldValue === undefined) {
            oldValue = null;
          } else if (typeof oldValue === 'string') {
            const trimmedOld = oldValue.trim();
            if (trimmedOld === '') {
              oldValue = null;
            } else {
              const parsedOld = parseFloat(trimmedOld);
              oldValue = Number.isNaN(parsedOld) ? null : parsedOld;
            }
          } else if (oldValue !== null && (typeof oldValue !== 'number' || Number.isNaN(oldValue))) {
            oldValue = null;
          }

          // Only update if the value has actually changed from the original
          if (newValue !== oldValue) {
            preserveCurrentPosition(cell);

            // Handle grouped rows - extract the correct consumption
            if (isGroupedConsumption(rowData)) {
              const plantId = getPlantIdFromField(columnField || '');
              if (!plantId) return;

              const consumption = rowData.consumptions.find((c) => c.plant.id === plantId);
              if (!consumption) return;

              handleUpdateConsumptionsRef.current([
                {
                  id: consumption.id,
                  nomination: newValue,
                  plantId: consumption.plant.id,
                  date: consumption.date instanceof Date ? consumption.date : new Date(consumption.date),
                },
              ]);
              return;
            }

            // Handle non-grouped rows
            const consumptionData = rowData as Consumption;
            handleUpdateConsumptionsRef.current([
              {
                id: consumptionData.id,
                nomination: newValue,
                plantId: consumptionData.plant.id,
                date: consumptionData.date instanceof Date ? consumptionData.date : new Date(consumptionData.date),
              },
            ]);
          }
        });

        saveTableInstance(table);
      }
    },
    [saveTableInstance]
  );

  const options: Options = useMemo(
    () => ({
      layout: 'fitColumns',
      selectableRange: 1,
      height,
      selectableRangeColumns: true,
      selectableRangeRows: false,
      selectableRangeClearCells: false, // Disable range clearing, we'll handle it manually
      editTriggerEvent: 'dblclick',
      // keybindings: false, // Disable Tabulator keybindings to allow arrow keys in editor
      clipboard: true,
      clipboardCopyStyled: false,
      clipboardCopyConfig: {
        rowHeaders: false,
        columnHeaders: false,
      },
      clipboardCopyRowRange: 'range',
      columnDefaults: {
        hozAlign: 'center',
        vertAlign: 'middle',
        headerHozAlign: 'center',
        headerVertAlign: 'middle',
      },
      clipboardPasteParser: (clipboard: string) => {
        // Get the table instance directly from the store to ensure we have the latest value
        const currentTableInstance = useConsumerTableStore.getState().tableInstance;
        if (!currentTableInstance) return [];

        const ranges = currentTableInstance.getRanges();
        if (!ranges || ranges.length === 0) return [];

        const range = ranges[0];
        const rows = range.getRows();
        const columnsInRange = range.getColumns();

        if (!rows || rows.length === 0 || !columnsInRange || columnsInRange.length === 0) return [];

        // Get the column field from the first column in the range
        const firstColumn = columnsInRange[0];
        const columnField = firstColumn.getField();

        // Only allow paste in nomination columns
        if (!isNominationColumn(columnField)) {
          return [];
        }

        // Check if the range contains only one column
        if (columnsInRange.length > 1) {
          // If multiple columns selected, only allow paste if all columns are nomination columns
          const allNominationColumns = columnsInRange.every((column) => isNominationColumn(column.getField()));
          if (!allNominationColumns) return [];
        }

        // Transform the pasted text to an array of values
        const transformedRows = transformPastedText(clipboard);
        const pastedRowsCount = transformedRows.length;
        const pastedColumnsCount = Math.max(0, ...transformedRows.map((row) => row.length));
        const selectedRowsLength = rows.length;

        if (pastedRowsCount === 0 || pastedColumnsCount === 0) return [];
        if (transformedRows.some((row) => row.some((value) => Number.isNaN(value)))) return [];

        const allColumns = currentTableInstance.getColumns();
        const firstColumnIndex = allColumns.findIndex((column) => column === firstColumn);
        let columnsToPaste = columnsInRange.slice(0, pastedColumnsCount);

        if (columnsToPaste.length < pastedColumnsCount && firstColumnIndex >= 0) {
          columnsToPaste = allColumns.slice(firstColumnIndex, firstColumnIndex + pastedColumnsCount);
        }

        if (columnsToPaste.length < pastedColumnsCount) return [];
        if (!columnsToPaste.every((column) => isNominationColumn(column.getField()))) return [];

        const addBackendUpdate = (
          updates: Array<{
            id: number;
            nomination: number | null;
            plantId: number;
            date: Date;
          }>,
          rowData: ConsumptionTableRow,
          columnFieldToUpdate: string | undefined,
          valueToPaste: PastedCellValue
        ) => {
          if (!columnFieldToUpdate) return;

          if (isGroupedConsumption(rowData)) {
            const plantId = getPlantIdFromField(columnFieldToUpdate);
            if (!plantId) return;

            const consumption = rowData.consumptions.find((c) => c.plant.id === plantId);
            if (!consumption) return;

            updates.push({
              id: consumption.id,
              nomination: valueToPaste,
              plantId: consumption.plant.id,
              date: consumption.date instanceof Date ? consumption.date : new Date(consumption.date),
            });
            return;
          }

          if (columnFieldToUpdate !== 'nomination') return;

          const consumptionData = rowData as Consumption;
          updates.push({
            id: consumptionData.id,
            nomination: valueToPaste,
            plantId: consumptionData.plant.id,
            date: consumptionData.date instanceof Date ? consumptionData.date : new Date(consumptionData.date),
          });
        };

        const buildUpdatedRowData = (rowData: ConsumptionTableRow, rowValues: PastedCellValue[]) =>
          columnsToPaste.reduce<ConsumptionTableRow & Record<string, unknown>>(
            (updatedData, column, columnIndex) => {
              if (columnIndex >= rowValues.length) return updatedData;

              const field = column.getField();
              if (!field) return updatedData;

              const pastedValue = rowValues[columnIndex];

              // eslint-disable-next-line no-param-reassign
              updatedData[field] = pastedValue;

              if (isGroupedConsumption(updatedData)) {
                const plantId = getPlantIdFromField(field);
                if (plantId) {
                  // eslint-disable-next-line no-param-reassign
                  updatedData.consumptions = updatedData.consumptions.map((consumption) =>
                    consumption.plant.id === plantId ? { ...consumption, nomination: pastedValue } : consumption
                  );
                }
              }

              return updatedData;
            },
            { ...rowData }
          );

        // If only one cell is selected but multiple values are pasted,
        // expand to include all necessary rows from the table
        if (selectedRowsLength === 1 && pastedRowsCount > 1) {
          // Get the first selected row
          const firstRow = rows[0];
          const firstRowIndex = firstRow.getPosition();

          // Check if getPosition returned a valid number
          if (typeof firstRowIndex !== 'number') {
            return [];
          }

          // Get all rows from the table starting from the selected row
          const allRows = currentTableInstance.getRows();

          // Ensure we start from the correct index (getPosition might be 0-based or 1-based)
          // We'll use the actual row object to find its index in the allRows array
          const actualIndex = allRows.findIndex((r) => r === firstRow);
          const startIndex = actualIndex !== -1 ? actualIndex : firstRowIndex;

          const rowsFromPosition = allRows.slice(startIndex, startIndex + pastedRowsCount);

          // Only use rows that exist (don't exceed table bounds)
          if (rowsFromPosition.length < pastedRowsCount) {
            // Not enough rows available, return empty to prevent partial paste
            return [];
          }

          // Prepare all updates to send to the backend
          const updatesToSend: Array<{
            id: number;
            nomination: number | null;
            plantId: number;
            date: Date;
          }> = [];

          rowsFromPosition.forEach((row, rowIndex) => {
            const pastedValues = transformedRows[rowIndex];
            const rowData = row.getData() as ConsumptionTableRow;

            columnsToPaste.forEach((column, columnIndex) => {
              if (columnIndex >= pastedValues.length) return;
              addBackendUpdate(updatesToSend, rowData, column.getField(), pastedValues[columnIndex]);
            });
          });

          // If no valid updates were created, return empty
          if (updatesToSend.length === 0) {
            return [];
          }

          // Update all rows manually using Tabulator API (including the first one)
          // Use requestAnimationFrame to ensure updates happen after Tabulator processes the paste
          requestAnimationFrame(() => {
            // Update UI first for immediate feedback
            rowsFromPosition.forEach((row, rowIndex) => {
              const rowData = row.getData() as ConsumptionTableRow;

              row.update(buildUpdatedRowData(rowData, transformedRows[rowIndex]));
            });

            // Then update all consumptions in the backend at once
            handleUpdateConsumptionsRef.current(updatesToSend);
          });

          // Return empty array to prevent Tabulator's default paste behavior
          // We've already updated all rows manually above
          return [];
        }

        // If multiple rows selected, they must match the pasted count
        if (selectedRowsLength !== pastedRowsCount) {
          return [];
        }

        // Build the result array and prepare backend updates
        const backendUpdates: Array<{
          id: number;
          nomination: number | null;
          plantId: number;
          date: Date;
        }> = [];

        const result = rows.map((row, rowIndex) => {
          const rowData = row.getData() as ConsumptionTableRow;
          const pastedValues = transformedRows[rowIndex];

          columnsToPaste.forEach((column, columnIndex) => {
            if (columnIndex >= pastedValues.length) return;
            addBackendUpdate(backendUpdates, rowData, column.getField(), pastedValues[columnIndex]);
          });

          return buildUpdatedRowData(rowData, pastedValues);
        });

        // Send updates to backend
        if (backendUpdates.length > 0) {
          requestAnimationFrame(() => {
            handleUpdateConsumptionsRef.current(backendUpdates);
          });
        }

        return result;
      },
      clipboardPasteAction: 'range',
      cellEditing: (cell: CellComponent) => {
        // Prevent editing non-editable columns (only nomination columns are editable)
        const column = cell.getColumn();
        const columnField = column.getField();

        if (!isNominationColumn(columnField)) {
          // Cancel editing if trying to edit a non-editable column
          cell.cancelEdit();
        }
      },
      cellEdited: (cell: CellComponent) => {
        // Prevent editing non-editable columns (only nomination columns are editable)
        // This is a fallback in case cellEditing didn't catch it
        const column = cell.getColumn();
        const columnField = column.getField();

        if (!isNominationColumn(columnField)) {
          // Restore the original value if trying to edit a non-editable column
          cell.restoreOldValue();
        }
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [height]
  );

  // Update Tabulator columns when columns change and instance exists
  useEffect(() => {
    if (tableInstance && columns.length > 0) {
      // Use setTimeout to ensure Tabulator is ready
      const timeoutId = setTimeout(() => {
        tableInstance.setColumns(columns);
        tableInstance.redraw(false);
      }, 0);

      return () => {
        clearTimeout(timeoutId);
      };
    }
    return undefined;
  }, [tableInstance, columns]);

  // Resize handlers
  const handleSetHeight = useCallback(
    (h: number) => {
      if (tableInstance) {
        tableInstance.setHeight(h);
      }
    },
    [tableInstance]
  );

  const debouncedSetHeight = useDebounceCallback(handleSetHeight, 500);

  useEffect(() => {
    if (tableInstance && height > 0) {
      debouncedSetHeight(height);
    }
  }, [height, tableInstance, debouncedSetHeight]);

  return {
    columns,
    isUpdating,
    theme,
    tableInstance,
    options,
    getTableInstance,
  };
};
